import {
  Carousel,
  CarouselContent,
  NextButton,
  PreviousButton,
} from '@grupoboticario/flora-team-react-carousel';
import { useLocation } from 'react-router-dom';
import slugify from 'slugify';

import { CardContent, HeadingWithButton, ResponsiveFloraTypography } from 'components';
import { SLIDES_TO_SHOW } from 'config/environments/shared';
import { GenericContentProps } from 'domains/content';
import { isMobile as isMobileHelper } from 'helpers';
import { useTracking } from 'hooks';

import Styles from './ContentCarousel.styles';

export const ContentCarousel = ({
  data,
  showHeaderCarrossel,
  pageTitle,
}: {
  pageTitle?: string;
  data: GenericContentProps;
  showHeaderCarrossel: boolean;
}) => {
  const { listToShare } = data;
  const isMobile = isMobileHelper();
  const { pathname } = useLocation();
  const formattedPathname = slugify(pathname, {
    lower: true,
    replacement: '_',
    remove: /[*+~.()'"!:@/]/g,
  }).replace(/-/g, '_');
  const maxItemsOnCarousel = 12;
  const filteredItems = listToShare
    ? listToShare
        .slice(0, maxItemsOnCarousel)
        .filter((list: {}) => Object.keys(list).find((key) => key === 'image'))
    : [];
  const { sendDataToGTM } = useTracking();

  const handleCarouselInteraction = (event: React.MouseEvent<HTMLButtonElement> | number) => {
    const interactions: { [key: string]: string } = {
      prev: 'click:anterior',
      next: 'click:proximo',
    };

    const analyticsData = {
      event: 'analytics-event',
      event_parameters: {
        event_name: `interaction_${slugify(formattedPathname || '', {
          lower: true,
          replacement: '_',
        })}`,
        cd_section: slugify(data.title || '', { lower: true, replacement: '_' }),
      },
    };

    if (typeof event === 'number') {
      const index = event as number;

      sendDataToGTM({
        ...analyticsData,
        event_parameters: {
          ...analyticsData.event_parameters,
          cd_interaction_detail: `click:carrossel:${index + 1}`,
        },
      });
      return;
    }

    sendDataToGTM({
      ...analyticsData,
      event_parameters: {
        ...analyticsData.event_parameters,
        cd_interaction_detail: interactions[event.currentTarget.dataset.interaction || ''],
      },
    });
  };

  return (
    <Styles.Flex direction='column' gap='$3'>
      {showHeaderCarrossel && data.title ? <HeadingWithButton title={data.title || ''} /> : null}

      {showHeaderCarrossel && data.description ? (
        <ResponsiveFloraTypography fontSize='bodyLargeShort' fontWeight='medium'>
          {data.description}
        </ResponsiveFloraTypography>
      ) : null}

      {filteredItems.length <= SLIDES_TO_SHOW[isMobile ? 'mobile' : 'desktop'] ? (
        <Styles.WithoutCarouselWrapper>
          {filteredItems.map((item, index) => (
            <CardContent
              key={index}
              {...item}
              analyticsData={{
                index: index + 1,
                section: data?.title || data.description || pageTitle || '',
              }}
            />
          ))}
        </Styles.WithoutCarouselWrapper>
      ) : (
        <Styles.CarouselWrapper>
          <Carousel
            onDotPress={handleCarouselInteraction}
            onSlideChange={handleCarouselInteraction}
          >
            <PreviousButton onClick={handleCarouselInteraction} data-interaction='prev' />

            <CarouselContent
              slidesToShow={SLIDES_TO_SHOW[isMobile ? 'mobile' : 'desktop']}
              infinite={false}
            >
              {filteredItems.map((item, index) => (
                <CardContent
                  key={index}
                  {...item}
                  analyticsData={{
                    index: index + 1,
                    section: data?.title || data.description || pageTitle || '',
                  }}
                />
              ))}
            </CarouselContent>

            <NextButton onClick={handleCarouselInteraction} data-interaction='next' />
          </Carousel>
        </Styles.CarouselWrapper>
      )}
    </Styles.Flex>
  );
};
